import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import LandingLayout from './LandingLayout';
import TimezoneSelector from './TimezoneSelector';
import ProgressBar from './ProgressBar';

const LovedOneSetup: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [checkInFrequency, setCheckInFrequency] = useState('1');
  const [checkInTime1, setCheckInTime1] = useState('09:00');
  const [checkInTime2, setCheckInTime2] = useState('18:00');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Load form data from localStorage on component mount
  useEffect(() => {
    const savedCustomerInfo = localStorage.getItem('signupCustomerInfo');
    if (savedCustomerInfo) {
      try {
        const customerInfo = JSON.parse(savedCustomerInfo);
        if (customerInfo.name) {
          const nameParts = customerInfo.name.split(' ');
          if (nameParts.length >= 2) {
            setFirstName(nameParts[0]);
            setLastName(nameParts.slice(1).join(' '));
          } else if (nameParts.length === 1) {
            setFirstName(nameParts[0]);
          }
        }
        if (customerInfo.phoneNumber) setPhoneNumber(customerInfo.phoneNumber);
        if (customerInfo.timezone) setTimezone(customerInfo.timezone);
        if (customerInfo.checkInFrequency) setCheckInFrequency(customerInfo.checkInFrequency);
        if (customerInfo.checkInTime1) setCheckInTime1(customerInfo.checkInTime1);
        if (customerInfo.checkInTime2) setCheckInTime2(customerInfo.checkInTime2);
        if (customerInfo.customMessage) setCustomMessage(customerInfo.customMessage);
      } catch (err) {
        console.error('Error loading saved customer info:', err);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!firstName.trim()) {
      setError('First name is required');
      setLoading(false);
      return;
    }

    if (!lastName.trim()) {
      setError('Last name is required');
      setLoading(false);
      return;
    }

    if (!phoneNumber.trim()) {
      setError('Phone number is required');
      setLoading(false);
      return;
    }

    // Basic phone number validation
    const phoneRegex = /^\+?1?\s*\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
    if (!phoneRegex.test(phoneNumber.trim())) {
      setError('Please enter a valid phone number (e.g., +1 (555) 123-4567)');
      setLoading(false);
      return;
    }

    try {
      // Save loved one information to localStorage for the next step
      // No database write until payment completion
      const userInfo = JSON.parse(localStorage.getItem('signupUserInfo') || '{}');
      
      const customerInfo = {
        name: `${firstName.trim()} ${lastName.trim()}`,
        phoneNumber: phoneNumber.trim(),
        timezone,
        checkInFrequency,
        checkInTime1,
        checkInTime2,
        customMessage,
        caregiverName: userInfo.firstName ? `${userInfo.firstName} ${userInfo.lastName}` : 'Primary Caregiver',
        caregiverPhone: userInfo.phoneNumber || '',
        caregiverEmail: userInfo.email || ''
      };
      
      localStorage.setItem('signupCustomerInfo', JSON.stringify(customerInfo));
      
      // Enhanced debug logging for time discrepancy investigation
      console.log('=== TIME INPUT DEBUG - LovedOneSetup ===');
      console.log('Raw time inputs:', {
        checkInTime1: checkInTime1,
        checkInTime2: checkInTime2,
        checkInFrequency: checkInFrequency,
        timezone: timezone
      });
      console.log('Time input types:', {
        checkInTime1_type: typeof checkInTime1,
        checkInTime2_type: typeof checkInTime2
      });
      console.log('Time input lengths:', {
        checkInTime1_length: checkInTime1?.length,
        checkInTime2_length: checkInTime2?.length
      });
      console.log('Final customerInfo object:', customerInfo);
      console.log('=== END TIME INPUT DEBUG ===');
      console.log('LovedOneSetup Debug - customerInfo.customMessage:', customerInfo.customMessage);
      console.log('LovedOneSetup Debug - localStorage signupCustomerInfo:', localStorage.getItem('signupCustomerInfo'));

      // Navigate to care circle setup
      navigate('/signup/care-circle');
    } catch (err) {
      setError('Failed to save loved one information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Save current form data before navigating back
    const customerInfo = {
      name: `${firstName.trim()} ${lastName.trim()}`,
      phoneNumber: phoneNumber.trim(),
      timezone,
      checkInFrequency,
      checkInTime1,
      checkInTime2,
      customMessage,
    };
    localStorage.setItem('signupCustomerInfo', JSON.stringify(customerInfo));
    navigate('/signup/account');
  };

  return (
    <LandingLayout>
      <Container component="main" maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 800, mb: 4 }}>
            <ProgressBar
              currentStep={3}
              totalSteps={5}
              stepLabels={['Plan', 'Account', 'Loved One', 'Care Circle', 'Payment']}
            />
          </Box>
          
          <Paper elevation={3} sx={{ p: 6, width: '100%', maxWidth: 700 }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
              Tell Us About Your Loved One
            </Typography>
            
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              We'll use this information to send daily wellness check-ins to your loved one
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="lovedOneFirstName"
                    label="First Name"
                    name="lovedOneFirstName"
                    autoComplete="given-name"
                    autoFocus
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="lovedOneLastName"
                    label="Last Name"
                    name="lovedOneLastName"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="lovedOnePhoneNumber"
                    label="Phone Number"
                    name="lovedOnePhoneNumber"
                    autoComplete="tel"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={loading}
                    helperText="This is where we'll send the daily wellness check-ins"
                    placeholder="+1 (555) 123-4567"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    Timezone
                  </Typography>
                  <TimezoneSelector
                    onTimezoneChange={(newTimezone) => setTimezone(newTimezone)}
                    showCurrentTime={true}
                    allowSave={false}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ fontSize: '1.1rem', fontWeight: 'bold', mb: 2 }}>
                      How many check-ins per day?
                    </FormLabel>
                    <RadioGroup
                      value={checkInFrequency}
                      onChange={(e) => setCheckInFrequency(e.target.value)}
                      row
                    >
                      <FormControlLabel value="1" control={<Radio />} label="Once daily" />
                      <FormControlLabel value="2" control={<Radio />} label="Twice daily" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="checkInTime1"
                    label="Check-in Time 1"
                    name="checkInTime1"
                    type="time"
                    value={checkInTime1}
                    onChange={(e) => {
                      console.log('Time 1 input changed:', e.target.value);
                      setCheckInTime1(e.target.value);
                    }}
                    disabled={loading}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                {checkInFrequency === '2' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      id="checkInTime2"
                      label="Check-in Time 2"
                      name="checkInTime2"
                      type="time"
                      value={checkInTime2}
                      onChange={(e) => {
                        console.log('Time 2 input changed:', e.target.value);
                        setCheckInTime2(e.target.value);
                      }}
                      disabled={loading}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="customMessage"
                    label="Custom Message (Optional)"
                    name="customMessage"
                    multiline
                    rows={3}
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    disabled={loading}
                    helperText="Add a personal message like 'I love you' or 'Remember to take your pills'"
                    placeholder="I love you and want to make sure you're doing well today!"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                  disabled={loading}
                  sx={{
                    borderColor: '#2E7D32',
                    color: '#2E7D32',
                    '&:hover': {
                      borderColor: '#1B5E20',
                      backgroundColor: 'rgba(46, 125, 50, 0.04)',
                    },
                  }}
                >
                  Back
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  endIcon={<ArrowForward />}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    backgroundColor: '#2E7D32',
                    '&:hover': {
                      backgroundColor: '#1B5E20'
                    },
                    fontSize: '1.1rem',
                  }}
                >
                  {loading ? 'Saving...' : 'Next'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </LandingLayout>
  );
};

export default LovedOneSetup;
